/**
 * Contact Grouping Rollback card — lives in /admin/diagnostics.
 *
 * Lists every contact-grouping migration batch (db.contact_grouping_migration_backups
 * aggregation) with a typed-confirmation rollback button. Tenant-scoped:
 * any authenticated user sees only batches for their own tenant — super-
 * admin on Keystone sees Keystone batches; tenant owners see their own.
 *
 * Rollback (POST /api/contact-grouping/rollback):
 *  1. Restores each contact's snapshotted `company` value.
 *  2. Clears `group_id`, `needs_completion`, `data_hygiene_suspect`.
 *  3. Deletes every contact_group with this batch_id (pre-existing groups
 *     created outside the migration are NEVER touched).
 *  4. Unlinks any contractor.group_id that pointed at a deleted group.
 *  5. Removes the backup snapshot rows themselves so a second rollback
 *     of the same batch_id cleanly 404s.
 *
 * Typed confirmation prevents fat-finger fires.
 *
 * data-testids:
 *   contact-grouping-rollback-card
 *   contact-grouping-rollback-toggle
 *   contact-grouping-batch-row-{batch_id_prefix}
 *   contact-grouping-rollback-confirm-{batch_id_prefix}
 *   contact-grouping-rollback-execute-{batch_id_prefix}
 *   no-grouping-batches-msg
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Layers, Loader2, AlertTriangle, RotateCcw, CheckCircle2,
  ChevronDown, ChevronRight,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function ContactGroupingRollbackCard() {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [confirmInput, setConfirmInput] = useState({});
  const [restoring, setRestoring] = useState(null);
  const [hidden, setHidden] = useState(false);

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/contact-grouping/batches`,
        { headers: authHeaders() },
      );
      setBatches(res.data?.batches || []);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setHidden(true);
        return;
      }
      toast.error(err.response?.data?.detail || 'Failed to load grouping batches');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (expanded) fetchBatches();
  }, [expanded, fetchBatches]);

  // Feb 2026 — Listen for the runner card's apply event. Auto-expand the
  // rollback card and refresh the batch list so the just-applied batch_id
  // appears immediately without a manual reload.
  useEffect(() => {
    const handler = () => {
      setExpanded(true);
      fetchBatches();
    };
    window.addEventListener('contact-grouping-batch-applied', handler);
    return () => window.removeEventListener('contact-grouping-batch-applied', handler);
  }, [fetchBatches]);

  const executeRollback = async (batchId) => {
    const typed = (confirmInput[batchId] || '').trim();
    if (typed !== batchId) {
      toast.error('Type the exact batch_id to confirm the rollback');
      return;
    }
    setRestoring(batchId);
    try {
      const res = await axios.post(
        `${API}/api/contact-grouping/rollback`,
        { batch_id: batchId },
        { headers: authHeaders() },
      );
      toast.success(
        `Rolled back ${res.data.contacts_restored} contact${res.data.contacts_restored === 1 ? '' : 's'} · ${res.data.groups_deleted} group${res.data.groups_deleted === 1 ? '' : 's'} deleted · ${res.data.contractors_unlinked_from_deleted_groups} contractor link${res.data.contractors_unlinked_from_deleted_groups === 1 ? '' : 's'} removed.`,
      );
      setConfirmInput(prev => ({ ...prev, [batchId]: '' }));
      await fetchBatches();
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Batch not found (already rolled back?). Refreshing list.');
        await fetchBatches();
      } else {
        toast.error(err.response?.data?.detail || 'Rollback failed');
      }
    } finally {
      setRestoring(null);
    }
  };

  if (hidden) return null;

  return (
    <Card className="border-rose-200" data-testid="contact-grouping-rollback-card">
      <CardHeader
        className="cursor-pointer flex flex-row items-start justify-between gap-4"
        onClick={() => setExpanded(!expanded)}
        data-testid="contact-grouping-rollback-toggle"
      >
        <div className="flex items-start gap-3">
          {expanded ? <ChevronDown className="h-5 w-5 mt-0.5" /> : <ChevronRight className="h-5 w-5 mt-0.5" />}
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Contact Grouping Rollback
              <Badge className="bg-rose-100 text-rose-900 border-rose-300 border ml-1">
                destructive
              </Badge>
            </CardTitle>
            <CardDescription className="mt-1">
              Reverse a contact-grouping migration batch. Restores company values,
              clears group_id / needs_completion / data_hygiene_suspect, deletes
              migration-created groups, unlinks contractors. Tenant-scoped.
              Typed-confirmation gated.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-4">
          {loading && (
            <p className="text-sm text-stone-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </p>
          )}
          {!loading && batches.length === 0 && (
            <p className="text-sm text-stone-500" data-testid="no-grouping-batches-msg">
              No contact-grouping migration batches found for this tenant.
            </p>
          )}
          {batches.map(b => {
            const typed = (confirmInput[b.batch_id] || '').trim();
            const canRestore = typed === b.batch_id;
            const idPrefix = b.batch_id.slice(0, 24);
            return (
              <div
                key={b.batch_id}
                className="rounded-md border border-stone-200 bg-stone-50 p-3 space-y-2"
                data-testid={`contact-grouping-batch-row-${idPrefix}`}
              >
                <div className="text-sm space-y-0.5">
                  <p className="font-mono text-xs text-stone-700 break-all">{b.batch_id}</p>
                  <p className="text-stone-600">
                    {b.snapshot_count} snapshot{b.snapshot_count === 1 ? '' : 's'} ·
                    captured {b.created_at ? new Date(b.created_at).toLocaleString() : 'unknown'}
                  </p>
                  <p className="text-emerald-700 text-xs flex items-center gap-1 mt-1">
                    <CheckCircle2 className="h-3 w-3" /> Rollback restores company / clears group_id, needs_completion, data_hygiene_suspect / deletes groups created in this batch / unlinks contractors.
                  </p>
                </div>
                <div className="pt-2 border-t border-stone-200 space-y-2">
                  <p className="text-amber-900 text-xs flex items-start gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-px" />
                    To roll back, type the batch_id exactly to confirm:
                  </p>
                  <Input
                    value={typed}
                    onChange={(e) => setConfirmInput(prev => ({ ...prev, [b.batch_id]: e.target.value }))}
                    placeholder="paste / type the batch_id"
                    className="font-mono text-xs"
                    data-testid={`contact-grouping-rollback-confirm-${idPrefix}`}
                  />
                  <Button
                    size="sm"
                    disabled={!canRestore || restoring === b.batch_id}
                    onClick={() => executeRollback(b.batch_id)}
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    data-testid={`contact-grouping-rollback-execute-${idPrefix}`}
                  >
                    {restoring === b.batch_id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                    ) : (
                      <RotateCcw className="h-3.5 w-3.5 mr-1" />
                    )}
                    Roll back this batch
                  </Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      )}
    </Card>
  );
}
